import { useApolloClient } from '@apollo/client/react';
import { css } from '@emotion/css';
import { AuthLoadingScreen } from '@medcom/shared-ui/components/AuthLoadingScreen.tsx';
import { App, Flex, Layout, Result, theme } from 'antd';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router';
import { validate as isUuid } from 'uuid';
import { AppFrame } from '../AppFrame';
import { useAppStore } from '../hooks/store/useAppStore';
import { NoAccessRoute } from '../NoAccessRoute';
import { openRedeemed } from './openRedeemed';
import { type AisImportResult, AisRedeemError, redeemAisContext } from './redeemAisContext';
import { switchToZuweiser } from './switchToZuweiser';
import { useAisIdentity } from './useAisIdentity';

const ERROR_TEXTS = {
  expired: 'Der Link ist abgelaufen. Bitte die Anforderung erneut aus dem AIS öffnen.',
  foreign: 'Bitte die Anforderung erneut aus dem AIS öffnen.',
  failed: 'Die Anforderung konnte nicht geöffnet werden. Bitte erneut aus dem AIS öffnen.',
};

type LaunchError = keyof typeof ERROR_TEXTS | 'noAccess';
type LaunchStep = 'invalid' | 'foreign' | 'redeem';

const REDEEM_ERRORS: Record<number, LaunchError> = { 403: 'noAccess', 404: 'expired', 409: 'foreign' };

const errorKind = (e: unknown): LaunchError => (e instanceof AisRedeemError && REDEEM_ERRORS[e.status]) || 'failed';

const useLaunchStep = (): { step: LaunchStep; contextId: string } => {
  const identity = useAisIdentity();
  const [params] = useSearchParams();
  const vendor = params.get('vendor') ?? '';
  const contextId = params.get('ctx') ?? '';
  if (vendor === '' || !isUuid(contextId)) {
    return { step: 'invalid', contextId };
  }
  return { step: identity?.vendor === vendor ? 'redeem' : 'foreign', contextId };
};

// The takeover switches to this Zuweiser without asking; a started Anforderung asks first and keeps its Zuweiser
const zuweiserOf = (result: AisImportResult): string | null =>
  result.openRequestId || useAppStore.getState().patientData ? null : result.switchToDoctorId;

const Framed = ({ children }: { children: ReactNode }) => (
  <AppFrame>
    <Layout.Content>{children}</Layout.Content>
  </AppFrame>
);

const LaunchFailed = ({ text }: { text: string }) => {
  const { token } = theme.useToken();
  const containerStyle = css`
    padding: ${token.paddingXL}px;
  `;
  return (
    <Flex justify="center" className={containerStyle}>
      <Result status="warning" title="Laboranforderung aus dem AIS" subTitle={text} />
    </Flex>
  );
};

// Keycloak's answer to a refused link; shown without a sign-in, which would end on the medcom login form
export const AisLaunchFailed = ({ reason }: { reason: 'source' | 'expired' }) => (
  <LaunchFailed text={ERROR_TEXTS[reason === 'source' ? 'foreign' : 'expired']} />
);

export const AisLaunch = () => {
  const auth = useAuth();
  const { step, contextId } = useLaunchStep();
  const navigate = useNavigate();
  const client = useApolloClient();
  const setTempAisImportResult = useAppStore(state => state.setTempAisImportResult);
  const { modal, message } = App.useApp();
  const [error, setError] = useState<LaunchError | null>(null);
  const started = useRef(false);
  const user = auth.user;
  const accessToken = user?.access_token ?? '';

  useEffect(() => {
    if (started.current || step === 'invalid') return;
    started.current = true;
    if (step === 'foreign') {
      setError('foreign');
    } else {
      redeemAisContext(accessToken, contextId)
        .then(async result => {
          const doctorId = zuweiserOf(result);
          if (doctorId) {
            await switchToZuweiser(doctorId, client, user);
          }
          await openRedeemed(result, client, navigate, setTempAisImportResult, modal, message, user);
        })
        .catch(e => setError(errorKind(e)));
    }
  }, [step, contextId, accessToken, navigate, client, setTempAisImportResult, modal, message, user]);

  if (error === 'noAccess') {
    return (
      <Framed>
        <NoAccessRoute />
      </Framed>
    );
  }

  if (step === 'invalid' || error) {
    return (
      <Framed>
        <LaunchFailed text={ERROR_TEXTS[error ?? 'expired']} />
      </Framed>
    );
  }

  // outside the frame: the app appears only once it is in the Zuweiser's theme
  return <AuthLoadingScreen signinRetry={false} />;
};
